<script setup lang="ts">
import type {
  EventTier,
  GuestIn,
  PublicEventConfig,
  RegistrationCheckout,
  RegistrationQuote,
} from "@shared/types";
import { useBloxRuntime } from "@bagelink/blox";
import {
  ArrayInput,
  Btn,
  Card,
  Checkbox,
  EmailInput,
  EmptyState,
  FieldSetVue,
  Icon,
  NumberInput,
  SelectInput,
  TelInput,
  TextInput,
} from "@bagelink/vue";
import axiosClient from "axios";
import {
  computed,
  defineAsyncComponent,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { registration } from "@shared/api";
import {
  createEmptyGuest,
  dollarsToMinorUnits,
  guestsAreValid,
  normalizeGuests,
} from "../registration-form";

const props = withDefaults(
  defineProps<{
    mode?:
      | "registration"
      | "donation"
      | "unable-to-attend"
      | "young-leadership"
      | "after-party";
    viewOnly?: boolean;
    eyebrow?: string;
    title?: string;
    specialHeading?: boolean;
    intro?: string;
    optionsHeading?: string;
    optionLayout?: "cards" | "select";
    showUnableToAttend?: boolean;
    unableToAttendHeading?: string;
    unableToAttendText?: string;
    donationAmountLabel?: string;
    tierLabel?: string;
    quantityLabel?: string;
    maxQuantity?: number;
    showGuests?: boolean;
    maxGuests?: number;
    guestHelpText?: string;
    showAdditionalDonation?: boolean;
    collectAddress?: boolean;
    showAnonymous?: boolean;
    showNewsletter?: boolean;
    showJournalAdHelp?: boolean;
    journalAdText?: string;
    journalAdUrl?: string;
    showBillMeLater?: boolean;
    billMeLaterText?: string;
    deadlineText?: string;
    helpText?: string;
    legalText?: string;
    contactName?: string;
    contactEmail?: string;
    contactPhone?: string;
    contactUrl?: string;
    successPath?: string;
  }>(),
  {
    mode: "registration",
    viewOnly: false,
    eyebrow: "",
    title: "",
    specialHeading: true,
    intro: "",
    optionsHeading: "",
    optionLayout: "cards",
    showUnableToAttend: true,
    unableToAttendHeading: "Can’t attend",
    unableToAttendText:
      "I can’t attend the event, but would still like to make a donation.",
    donationAmountLabel: "Donation amount",
    tierLabel: "Registration or support level",
    quantityLabel: "Quantity",
    maxQuantity: 10,
    showGuests: true,
    maxGuests: 10,
    guestHelpText:
      "Add each attendee separately. Email and phone are optional; providing either lets Shalva connect this attendee across future events and avoid duplicate records.",
    showAdditionalDonation: true,
    collectAddress: true,
    showAnonymous: true,
    showNewsletter: true,
    showJournalAdHelp: false,
    journalAdText:
      "Journal ad copy and artwork are collected separately after checkout.",
    journalAdUrl: "",
    showBillMeLater: false,
    billMeLaterText:
      "To make a pledge or request an invoice, contact Shalva. This form only processes secure card payments.",
    deadlineText: "",
    helpText: "",
    legalText: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    contactUrl: "https://www.shalva.org/contact-us/",
    successPath: "/thank-you",
  },
);

const { isPreview } = useBloxRuntime();
const PaymentElement = defineAsyncComponent(
  () => import("./PaymentElement.vue"),
);
const config = ref<PublicEventConfig>();
type RegistrationQuoteResponse = RegistrationQuote & {
  $raw?: { data?: RegistrationQuote };
};
const quote = ref<RegistrationQuoteResponse>();
const checkout = ref<RegistrationCheckout>();
const payment = ref<{ confirm: () => Promise<void> }>();
// Render CMS-owned options during SSR. Operational config is loaded after
// hydration and only enables/disables checkout; it must not gate page content.
const phase = ref<"details" | "payment">("details");
const error = ref("");
const busy = ref(false);
let quoteTimer: ReturnType<typeof setTimeout> | undefined;

const form = reactive({
  tierKey: "",
  quantity: 1,
  additionalDonation: 0,
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  streetAddress: "",
  aptSuite: "",
  city: "",
  state: "",
  zipCode: "",
  guests: [] as GuestIn[],
  anonymous: false,
  newsletterOptIn: false,
});

const options = computed<EventTier[]>(() => config.value?.options ?? []);
const tierOptions = computed(() =>
  options.value.map((option) => ({ label: option.name, value: option.key })),
);
const selectedTier = computed(() =>
  options.value.find((option) => option.key === form.tierKey),
);
const allowQuantity = computed(
  () => selectedTier.value?.allow_quantity === true,
);
const effectiveMaxQuantity = computed(() =>
  allowQuantity.value
    ? (selectedTier.value?.max_quantity ?? props.maxQuantity)
    : 1,
);
const donationOnly = computed(
  () => props.mode === "donation" || props.mode === "unable-to-attend",
);
const openDonationSelected = computed(
  () =>
    donationOnly.value ||
    form.tierKey === "__unable__" ||
    selectedTier.value?.unit_amount === 0,
);
const unableTierKey = computed(
  () =>
    options.value.find((option) => option.unit_amount === 0)?.key ??
    "__unable__",
);
const canCheckout = computed(
  () => !isPreview.value && Boolean(config.value && form.tierKey),
);
const optionsHeading = computed(
  () =>
    props.optionsHeading ||
    {
      registration: "Registration and support options",
      donation: "Donation options",
      "unable-to-attend": "Support Shalva from afar",
      "young-leadership": "Young Leadership options",
      "after-party": "After-party options",
    }[props.mode],
);
const localTotal = computed(
  () =>
    (selectedTier.value?.unit_amount ?? 0) * Math.max(1, form.quantity) +
    dollarsToMinorUnits(form.additionalDonation),
);
const quoteTotal = computed(
  () => quote.value?.$raw?.data?.total ?? quote.value?.total,
);
const totalLabel = computed(() =>
  quoteTotal.value !== undefined
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency:
          quote.value?.currency || selectedTier.value?.currency || "USD",
      }).format(quoteTotal.value / 100)
    : (selectedTier.value || openDonationSelected.value) &&
        (selectedTier.value || form.additionalDonation > 0)
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: selectedTier.value?.currency ?? "USD",
        }).format(localTotal.value / 100)
      : "Calculating…",
);
const paymentReturnUrl = computed(() =>
  checkout.value
    ? `${window.location.origin}${props.successPath}?registration_id=${checkout.value.registration_id}`
    : "",
);
const stripeBillingDetails = computed(() => ({
  name: `${form.firstName} ${form.lastName}`.trim(),
  email: form.email,
  phone: form.phone,
  address: {
    line1: form.streetAddress,
    ...(form.aptSuite ? { line2: form.aptSuite } : {}),
    city: form.city,
    state: form.state,
    postal_code: form.zipCode,
    country: "US",
  },
}));
onMounted(loadConfig);
watch(
  () => [form.tierKey, form.quantity, form.additionalDonation],
  scheduleQuote,
);
watch(
  () => effectiveMaxQuantity.value,
  (max) => {
    form.quantity = Math.min(Math.max(1, form.quantity), max);
  },
);

async function loadConfig() {
  error.value = "";
  try {
    config.value = await registration.config({});
    form.tierKey = "";
    phase.value = "details";
    if (!props.viewOnly && form.tierKey) await refreshQuote();
  } catch (cause) {
    if (axiosClient.isAxiosError(cause) && cause.response?.status === 404) {
      config.value = {
        event_key: "shalva_current_event",
        stripe_publishable_key: "",
        options: [],
      };
      phase.value = "details";
      form.tierKey = "";
      return;
    }
    if (isPreview.value && options.value.length) {
      config.value = {
        event_key: "editor_preview",
        stripe_publishable_key: "",
        options: [],
      };
      form.tierKey = "";
      phase.value = "details";
      error.value = "";
      return;
    }
    error.value =
      "Registration details are temporarily unavailable. Please try again.";
  }
}

function scheduleQuote() {
  clearTimeout(quoteTimer);
  quote.value = undefined;
  if (!canCheckout.value || props.viewOnly) return;
  if (form.tierKey === "__unable__") return;
  quoteTimer = setTimeout(refreshQuote, 250);
}

function optionAmountLabel(option: EventTier) {
  if (option.unit_amount === 0) return "Choose amount";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: option.currency,
    maximumFractionDigits: 0,
  }).format(option.unit_amount / 100);
}

function selectTier(key: string) {
  form.tierKey = key;
  form.quantity = 1;
  const option = options.value.find((candidate) => candidate.key === key);
  if (key === "__unable__" || option?.unit_amount === 0) {
    form.guests = [];
  }
  document
    .querySelector("#registration-details")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function contactHref(kind: "email" | "phone") {
  const value = kind === "email" ? props.contactEmail : props.contactPhone;
  if (!value) return "";
  return kind === "email"
    ? `mailto:${value}`
    : `tel:${value.replace(/[^\d+]/g, "")}`;
}

async function refreshQuote() {
  if (!canCheckout.value || !config.value || !form.tierKey) return;
  if (form.tierKey === "__unable__") {
    quote.value = undefined;
    return;
  }
  try {
    quote.value = await registration.quote({
      registrationQuoteBody: {
        registrationQuoteRequest: {
          event_key: config.value.event_key,
          tier_key: form.tierKey,
          quantity: Math.max(1, form.quantity),
          additional_donation: dollarsToMinorUnits(form.additionalDonation),
        },
      },
    });
    error.value = "";
  } catch {
    error.value = "We could not calculate the total. No payment has been made.";
  }
}

async function continueToPayment() {
  if (form.tierKey === "__unable__") {
    error.value =
      "Open-amount donations require a live donation option from the server before secure checkout can begin.";
    return;
  }
  if (!canCheckout.value) {
    error.value = isPreview.value
      ? "Editor preview only. Checkout is disabled in the designer."
      : "Checkout is currently unavailable.";
    return;
  }
  if (
    !quote.value ||
    !form.firstName ||
    !form.lastName ||
    !form.email ||
    !form.phone
  ) {
    error.value = "Complete all required contact fields before continuing.";
    return;
  }
  if (
    props.collectAddress &&
    (!form.streetAddress || !form.city || !form.state || !form.zipCode)
  ) {
    error.value =
      "Complete all required billing address fields before continuing.";
    return;
  }
  if (props.showGuests && !guestsAreValid(form.guests)) {
    error.value =
      "Enter a first and last name for each attendee, or remove incomplete attendees.";
    return;
  }
  if (props.showGuests && form.guests.length > props.maxGuests) {
    error.value = `Add no more than ${props.maxGuests} additional attendees.`;
    return;
  }
  if (form.quantity > effectiveMaxQuantity.value) {
    error.value = `Select no more than ${effectiveMaxQuantity.value} items.`;
    return;
  }
  busy.value = true;
  error.value = "";
  try {
    checkout.value = await registration.checkout({
      registrationCheckoutBody: {
        registrationCheckoutRequest: {
          quote_id: quote.value.quote_id,
          registrant: {
            first_name: form.firstName,
            last_name: form.lastName,
            email: form.email,
            phone: form.phone,
            billing_address: {
              street_address: form.streetAddress,
              apartment: form.aptSuite || null,
              city: form.city,
              state: form.state,
              zip_code: form.zipCode,
              country: "US",
            },
          },
          guests:
            props.showGuests && !openDonationSelected.value
              ? normalizeGuests(form.guests)
              : [],
          anonymous: props.showAnonymous && form.anonymous,
          newsletter_opt_in: props.showNewsletter && form.newsletterOptIn,
          idempotency_key: crypto.randomUUID(),
        },
      },
    });
    phase.value = "payment";
  } catch {
    error.value =
      "We could not prepare secure payment. Review your information and retry.";
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div>
    <header v-if="eyebrow || title || intro" class="flow-header mx-auto">
      <p v-if="eyebrow" class="m-0 pb-05 uppercase tracking-wide semi txt14">
        {{ eyebrow }}
      </p>
      <h1
        v-if="title"
        class="m-0 line-height-1 balance txt-center"
        :class="{ 'blox-special-heading': specialHeading }"
      >
        {{ title }}
      </h1>
      <p v-if="intro" class="m-0 pt-1 pretty txt-center">{{ intro }}</p>
    </header>

    <div
      v-if="error"
      class="flex gap-05 bg-red radius-2 p-1 space-between color-white m_column mb-1 w1000px"
      role="alert"
    >
      <p class="m-0">{{ error }}</p>
      <Btn value="Try again" @click="loadConfig" />
    </div>

    <section
      v-if="phase === 'details' && viewOnly"
      aria-labelledby="support-options-title"
    >
      <h2
        id="support-options-title"
        class="m-0 pb-2 txt-36 m_txt-28 line-height-12 m_line-height-13 balance txt-center"
      >
        {{ optionsHeading }}
      </h2>
      <p v-if="deadlineText" class="m-0 pb-1 txt-center pretty">
        <strong>{{ deadlineText }}</strong>
      </p>
      <div v-if="options.length" class="grid grid-wrap-3 gap-1 m_gap-1">
        <Card
          v-for="option in options"
          :key="option.key"
          class="grid gap-075 txt-center"
        >
          <h3 class="m-0 txt-24 line-height-13 balance">{{ option.name }}</h3>
          <strong class="txt-28 line-height-12">{{
            optionAmountLabel(option)
          }}</strong>
          <p v-if="option.description" class="m-0 pretty">
            {{ option.description }}
          </p>
          <ul v-if="option.includes.length" class="m-0 ps-1 txt-start">
            <li v-for="item in option.includes" :key="item">{{ item }}</li>
          </ul>
        </Card>
      </div>
      <EmptyState v-else icon="volunteer_activism">
        <span>Support options will appear here when they are configured.</span>
      </EmptyState>
      <div
        v-if="showJournalAdHelp || showBillMeLater || helpText || legalText"
        class="grid gap-075 pt-2 txt-center"
      >
        <p v-if="showJournalAdHelp" class="m-0 pretty">
          {{ journalAdText }}
          <a v-if="journalAdUrl" :href="journalAdUrl"
            >Submit journal materials</a
          >
        </p>
        <p v-if="showBillMeLater" class="m-0 pretty">
          {{ billMeLaterText }}
          <a :href="contactUrl">Contact Shalva</a>
        </p>
        <p v-if="helpText" class="m-0 pretty">{{ helpText }}</p>
        <p v-if="legalText" class="m-0 txt14 color-gray pretty">
          {{ legalText }}
        </p>
      </div>
    </section>

    <form
      v-else-if="phase === 'details'"
      class="w-100p mx-auto"
      @submit.prevent="continueToPayment"
    >
      <section
        v-if="optionLayout === 'cards'"
        class="option-picker"
        :aria-labelledby="
          optionsHeading ? 'registration-options-title' : undefined
        "
      >
        <h2
          v-if="optionsHeading"
          id="registration-options-title"
          class="m-0 pb-1 txt-30 m_txt-28 line-height-12 m_line-height-13 balance txt-center"
        >
          {{ optionsHeading }}
        </h2>
        <div class="grid grid-wrap-3 gap-2 m_gap-075 w1000px mx-auto pt-1">
          <Card
            v-for="option in options"
            :key="option.key"
            thin
            role="button"
            :tabindex="0"
            class="relative hover hover-lift shadow-light txt-start"
            :class="
              form.tierKey === option.key
                ? 'border-2 border-primary bg-primary color-white keep-white shadow'
                : ''
            "
            :aria-pressed="form.tierKey === option.key"
            @click="selectTier(option.key)"
            @keydown.enter.prevent="selectTier(option.key)"
            @keydown.space.prevent="selectTier(option.key)"
          >
            <span
              v-if="form.tierKey === option.key"
              class="absolute bottom-025 end-1 flex gap-025 fit-content m-0 ms-auto pb-075 txt-10 bold uppercase tracking-wide"
              ><Icon name="check_circle" /> Selected</span
            >
            <strong class="txt-28 line-height-12">{{
              optionAmountLabel(option)
            }}</strong>
            <h3 class="m-0 pt-05 txt-22 line-height-13 balance light pb-1">
              {{ option.name }}
            </h3>
            <p v-if="option.description" class="m-0 pt-05 pretty">
              {{ option.description }}
            </p>
            <p v-if="option.includes.length" class="m-0 txt14 opacity-7">
              <span v-for="item in option.includes" :key="item">{{
                item
              }}</span>
            </p>
          </Card>
          <Card
            v-if="
              showUnableToAttend &&
              mode === 'registration' &&
              !options.some((option) => option.unit_amount === 0)
            "
            thin
            role="button"
            :tabindex="0"
            class="relative hover hover-lift shadow-light txt-start"
            :class="
              form.tierKey === '__unable__'
                ? 'border-2 border-primary bg-primary color-white keep-white shadow'
                : ''
            "
            :aria-pressed="form.tierKey === '__unable__'"
            @click="selectTier(unableTierKey)"
            @keydown.enter.prevent="selectTier(unableTierKey)"
            @keydown.space.prevent="selectTier(unableTierKey)"
          >
            <span
              v-if="form.tierKey === '__unable__'"
              class="flex gap-025 fit-content m-0 ms-auto pb-075 txt13 bold uppercase tracking-wide"
              ><Icon name="check_circle" /> Selected</span
            >
            <strong class="txt-28 line-height-12">{{
              unableToAttendHeading
            }}</strong>
            <p class="m-0 pt-05 pretty">{{ unableToAttendText }}</p>
          </Card>
        </div>
      </section>

      <div class="border w400px m_w200px my-4 m_my-3"></div>
      <Card
        id="registration-details"
        frame
        shadow
        class="grid gap-1-5 p-2 m_p-1-25 txt-start w1000px bg-gray-10 shadow grid-wrap-3 align-items-start border-primary-30"
      >
        <div class="grid-span-2 m_grid-span-1">
          <SelectInput
            v-if="optionLayout === 'select'"
            v-model="form.tierKey"
            :label="tierLabel"
            :options="tierOptions"
            required
            class="light-input"
          />
          <p v-if="isPreview" class="m-0 color-gray pretty">
            Editor preview. Checkout is disabled in the designer.
          </p>
          <div
            class="grid grid-template-columns-1 align-items-start gap-1-5 m_gap-05 w700px"
          >
            <div class="grid gap-2 min-w-0">
              <section class="grid gap-col-1">
                <div class="flex gap-075 pb-025">
                  <span
                    class="grid place-items-center flex-shrink-0 square-40 round bg-primary-light"
                    ><Icon name="person"
                  /></span>
                  <div>
                    <h2 class="m-0 txt-20 line-height-13">Your information</h2>
                    <p class="m-0 txt14 opacity-7">
                      Who should we contact about this registration?
                    </p>
                  </div>
                </div>
                <div class="grid grid-wrap-2 gap-col-1">
                  <TextInput
                    v-model="form.firstName"
                    label="First name"
                    autocomplete="given-name"
                    required
                    class="light-input"
                  />
                  <TextInput
                    v-model="form.lastName"
                    label="Last name"
                    autocomplete="family-name"
                    required
                    class="light-input"
                  />
                  <EmailInput
                    v-model="form.email"
                    label="Email"
                    autocomplete="email"
                    required
                    class="light-input"
                  />
                  <TelInput
                    v-model="form.phone"
                    label="Phone"
                    autocomplete="tel"
                    required
                    class="light-input"
                  />
                </div>
              </section>

              <section v-if="collectAddress" class="grid gap-col-1">
                <div class="flex gap-075 pb-025">
                  <span
                    class="grid place-items-center flex-shrink-0 square-40 round bg-primary-light"
                    ><Icon name="home"
                  /></span>
                  <div>
                    <h2 class="m-0 txt-20 line-height-13">Billing address</h2>
                    <p class="m-0 txt14 opacity-7">
                      Used for your payment receipt.
                    </p>
                  </div>
                </div>
                <div class="grid grid-wrap-2 gap-col-1">
                  <div
                    class="address-row grid gap-col-1 m_grid-template-columns-1"
                  >
                    <TextInput
                      v-model="form.streetAddress"
                      label="Street address"
                      autocomplete="address-line1"
                      required
                      class="light-input"
                    />
                    <TextInput
                      v-model="form.aptSuite"
                      label="Apt / Suite"
                      autocomplete="address-line2"
                      class="light-input"
                    />
                  </div>
                  <TextInput
                    v-model="form.city"
                    label="City"
                    autocomplete="address-level2"
                    required
                    class="light-input"
                  />
                  <TextInput
                    v-model="form.state"
                    label="State"
                    autocomplete="address-level1"
                    required
                    class="light-input"
                  />
                  <TextInput
                    v-model="form.zipCode"
                    label="ZIP code"
                    autocomplete="postal-code"
                    required
                    class="light-input"
                  />
                </div>
              </section>

              <section
                v-if="showGuests && !openDonationSelected"
                class="grid gap-1"
              >
                <div class="flex gap-075 pb-025">
                  <span
                    class="grid place-items-center flex-shrink-0 square-40 round bg-primary-light"
                    ><Icon name="groups"
                  /></span>
                  <div>
                    <h2 class="m-0 txt-20 line-height-13">
                      Additional attendees
                    </h2>
                    <p class="m-0 txt14 opacity-7">
                      Add guest details now, or continue without additional
                      attendees.
                    </p>
                  </div>
                </div>
                <ArrayInput
                  v-model="form.guests"
                  label="Attendees"
                  :helptext="`${guestHelpText} Maximum ${maxGuests} additional attendees.`"
                  item-label="Attendee"
                  add-text="Add attendee"
                  delete-tooltip="Remove attendee"
                  :create-item="createEmptyGuest"
                  :allow-add="form.guests.length < maxGuests"
                  allow-delete
                  collapsible
                >
                  <template #default="{ item, index }">
                    <Card
                      thin
                      :legend="`Attendee ${index + 1}`"
                      class="w-100p my-05"
                    >
                      <div class="grid grid-wrap-2 gap-col-1">
                        <TextInput
                          v-model="item.value.first_name"
                          label="First name"
                          autocomplete="given-name"
                          required
                          class="light-input"
                        />
                        <TextInput
                          v-model="item.value.last_name"
                          label="Last name"
                          autocomplete="family-name"
                          required
                          class="light-input"
                        />
                      </div>
                      <div class="grid grid-wrap-2 gap-col-1">
                        <EmailInput
                          v-model="item.value.email"
                          label="Email (optional)"
                          autocomplete="email"
                          class="light-input"
                        />
                        <TelInput
                          v-model="item.value.phone"
                          label="Phone (optional)"
                          autocomplete="tel"
                          class="light-input"
                        />
                      </div>
                    </Card>
                  </template>
                </ArrayInput>
              </section>

              <section
                v-if="showAnonymous || showNewsletter"
                class="grid gap-col-1"
              >
                <div class="flex gap-075 pb-025">
                  <span
                    class="grid place-items-center flex-shrink-0 square-40 round bg-primary-light"
                    ><Icon name="tune"
                  /></span>
                  <div>
                    <h2 class="m-0 txt-20 line-height-13">Preferences</h2>
                  </div>
                </div>
                <Checkbox
                  v-if="showAnonymous"
                  v-model="form.anonymous"
                  label="List this gift anonymously"
                />
                <Checkbox
                  v-if="showNewsletter"
                  v-model="form.newsletterOptIn"
                  label="Keep me informed about Shalva"
                />
              </section>
            </div>
          </div>
          <div class="registration-notes">
            <p v-if="showJournalAdHelp" class="m-0 pretty">
              {{ journalAdText }}
              <a v-if="journalAdUrl" :href="journalAdUrl"
                >Submit journal materials</a
              >
            </p>
            <p v-if="deadlineText" class="m-0 pt-05 pretty">
              <strong>{{ deadlineText }}</strong>
            </p>
            <p v-if="legalText" class="m-0 txt14 color-gray pretty">
              {{ legalText }}
            </p>
            <p v-if="helpText" class="m-0 txt14 pretty">{{ helpText }}</p>
            <p
              v-if="contactName || contactEmail || contactPhone"
              class="m-0 txt14 pretty"
            >
              Questions? Contact
              <strong v-if="contactName">{{ contactName }}</strong>
              <a v-if="contactEmail" :href="contactHref('email')">{{
                contactEmail
              }}</a>
              <a v-if="contactPhone" :href="contactHref('phone')">{{
                contactPhone
              }}</a>
            </p>
            <p v-if="showBillMeLater" class="m-0 txt14 pretty">
              {{ billMeLaterText }}
              <a :href="contactUrl">Contact Shalva</a>
            </p>
          </div>
        </div>
        <aside
          class="grid gap-05 txt-center bg-gray-30 border p-1 radius-2"
          aria-label="Donation summary"
        >
          <p class="m-0 txt12 bold tracking-wide uppercase opacity-7">
            Donation summary
          </p>
          <EmptyState v-if="!form.tierKey" icon="touch_app">
            <span>Select an option above to see your summary.</span>
          </EmptyState>
          <template v-else>
            <h2 class="m-0 line-height-13 balance">
              {{
                selectedTier?.name ||
                (openDonationSelected ? unableToAttendHeading : tierLabel)
              }}
            </h2>
            <p
              v-if="selectedTier?.description"
              class="m-0 txt14 opacity-8 pretty"
            >
              {{ selectedTier.description }}
            </p>
            <p v-if="selectedTier?.includes.length" class="m-0 ps-1">
              <span v-for="item in selectedTier.includes" :key="item">
                {{ item }}
              </span>
            </p>
            <div class="grid gap-col-1 py-1 border-top border-bottom">
              <NumberInput
                v-if="!openDonationSelected && allowQuantity"
                v-model="form.quantity"
                :label="quantityLabel"
                :min="1"
                :max="effectiveMaxQuantity"
                :helptext="`Maximum ${effectiveMaxQuantity}`"
                required
                class="light-input"
              />
              <NumberInput
                v-if="showAdditionalDonation || openDonationSelected"
                v-model="form.additionalDonation"
                :label="
                  openDonationSelected
                    ? donationAmountLabel
                    : 'Additional donation'
                "
                :min="openDonationSelected ? 1 : 0"
                :step="0.01"
                icon-start="attach_money"
                :helptext="
                  openDonationSelected
                    ? 'Enter the amount you would like to donate in US dollars.'
                    : 'Enter an amount in US dollars.'
                "
                :required="openDonationSelected"
                class="light-input"
              />
            </div>
            <div class="flex align-items-baseline space-between gap-1">
              <span class="semi">Total</span>
              <strong class="txt-28 line-height-11">{{ totalLabel }}</strong>
            </div>
            <Btn
              type="submit"
              :value="
                isPreview
                  ? 'Preview only — checkout disabled'
                  : 'Continue to secure payment'
              "
              :loading="busy"
              :disabled="
                busy ||
                !quote ||
                !canCheckout ||
                form.quantity > effectiveMaxQuantity
              "
            />
            <p
              class="flex justify-content-center gap-025 -mt-025 m-0 txt13 opacity-7"
            >
              <Icon name="lock" /> Secure checkout powered by Stripe
            </p>
          </template>
        </aside>
      </Card>
    </form>

    <section
      v-else-if="phase === 'payment' && checkout && config"
      class="grid gap-1-5 w-100p w-680px mx-auto p-2 m_p-1-25 bg-white border radius-3 shadow-light"
    >
      <Btn
        flat
        icon="arrow_back"
        value="Edit details"
        class="justify-self-start"
        :disabled="busy"
        @click="phase = 'details'"
      />
      <h2 class="m-0 line-height-13 balance">Secure payment</h2>
      <p>
        Total due: <strong>{{ totalLabel }}</strong>
      </p>
      <PaymentElement
        ref="payment"
        :publishable-key="config.stripe_publishable_key"
        :client-secret="checkout.payment_intent_client_secret"
        :return-url="paymentReturnUrl"
        :billing-details="stripeBillingDetails"
        @error="error = $event"
        @processing="busy = $event"
      />
      <Btn
        value="Complete payment"
        :loading="busy"
        :disabled="busy"
        @click="payment?.confirm()"
      />
      <p class="-mt-05 m-0 txt14 opacity-8">
        Payment details are encrypted and handled by Stripe. Shalva does not
        store card numbers.
      </p>
    </section>
  </div>
</template>
